<template>
  <v-toolbar
    class="top-toolbar"
    dense
    flat>

    <v-tooltip bottom>
      <v-btn
        @click="$emit('undo')"
        slot="activator"
        icon>
        <v-icon>undo</v-icon>
      </v-btn>
      <span>復原</span>
    </v-tooltip>

    <v-tooltip bottom>
      <v-btn
        @click="$emit('redo')"
        slot="activator"
        icon>
        <v-icon>redo</v-icon>
      </v-btn>
      <span>重做</span>
    </v-tooltip>

    <v-divider
      class="mx-3"
      inset
      vertical/>

    <v-select
      class="brush-size-select"
      ref="brushSizeSelect"
      v-model="brushWidth"
      color="black"
      :items="brushSizes"
      :attach="$parent.$el"
      :menu-props="{nudgeRight: brushSizeMenuLeft}"
      no-data-text=""
      persistent-hint
      single-line
      flat>
      <!-- // HTML that describe how select should render selected items -->
      <template slot="selection" slot-scope="data">
        {{data.item}} pixels
      </template>

      <!-- // HTML that describe how select should render items when the select is open` -->
      <div class="brush-size-option" slot="item" slot-scope="data">
        <div class="brush-size-option__text">{{data.item}} pixels</div>
        <whiteboard-brush-dot class="brush-size-option__dot" :size="data.item" :color="brushColor"/>
      </div>
    </v-select>

    <v-divider
      class="mx-3"
      inset
      vertical/>

    <v-slider
      class="zoom-slider"
      v-model="scale"
      color="black"
      thumb-color="accent"
      :max="maxScale"
      :min="minScale"
      append-icon="zoom_in"
      prepend-icon="zoom_out"
      @click:append="zoom(true)"
      @click:prepend="zoom(false)"/>

    <span class="text-no-wrap ml-2">{{scaleText}}</span>

    <v-spacer />

    <v-divider
      class="mx-3"
      inset
      vertical/>

    <v-tooltip bottom>
      <v-btn
        @click="previousPage"
        slot="activator"
        icon>
        <v-icon>keyboard_arrow_left</v-icon>
      </v-btn>
      <span>上一頁</span>
    </v-tooltip>

    <div class="title text-no-wrap">{{page+1}} / {{maxPage}}</div>

    <v-tooltip bottom>
      <v-btn
        @click="nextPage"
        slot="activator"
        icon>
        <v-icon>keyboard_arrow_right</v-icon>
      </v-btn>
      <span>下一頁</span>
    </v-tooltip>

    <v-divider
      class="mx-3"
      inset
      vertical/>

    <v-tooltip
      v-if="isElectron"
      class="ml-auto"
      bottom>
      <v-btn
        @click="$emit('save', saveFilename)"
        slot="activator"
        icon>
        <v-icon>save_alt</v-icon>
      </v-btn>
      <span>另存圖檔</span>
    </v-tooltip>

    <v-menu
      v-else
      v-model="filenameMenu"
      transition="slide-y-transition"
      :close-on-content-click="false"
      offset-y>
      <v-tooltip
        class="ml-auto"
        slot="activator"
        bottom>
        <v-btn
          @click.stop="filenameMenu = true"
          slot="activator"
          icon>
          <v-icon>save_alt</v-icon>
        </v-btn>
        <span>另存圖檔</span>
      </v-tooltip>

      <v-card class="pl-3 pr-3 pt-3">
        <v-text-field
          v-model="saveFilename"
          label="檔名"
          color="black" />

        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn
            @click="filenameMenu = false"
            small
            flat>
            取消
          </v-btn>
          <v-btn
            @click="onSaveFilenameConfirmed"
            small
            flat>
            確認
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-menu>

    <v-tooltip bottom v-if="isFullScreen">
      <v-btn
        slot="activator"
        @click="toggleFullScreen"
        icon>
        <v-icon>fullscreen_exit</v-icon>
      </v-btn>
      <span>取消全螢幕</span>
    </v-tooltip>

    <v-tooltip bottom v-else>
      <v-btn
        slot="activator"
        @click="toggleFullScreen"
        icon>
        <v-icon>fullscreen</v-icon>
      </v-btn>
      <span>全螢幕</span>
    </v-tooltip>

    <v-tooltip bottom>
      <v-btn
        @click="$eventBus.$emit('whiteboard-toggle-boards-panel')"
        slot="activator"
        icon>
        <v-icon>menu</v-icon>
      </v-btn>
      <span>白板圖</span>
    </v-tooltip>
  </v-toolbar>
</template>

<script>
import {mapState} from 'vuex';
import WhiteboardBrushDot from './WhiteboardBrushDot';

export default {
  components: {
    'whiteboard-brush-dot': WhiteboardBrushDot
  },

  data() {
    return {
      maxPage: 5,
      scaleUnit: 20,
      brushSizes: [2, 5, 10, 15, 20, 30],
      isFullScreen: false,
      // This variable is uesed to fix incorrect position
      // of v-menu which was attached on none-app element
      brushSizeMenuLeft: 0,

      filenameMenu: false,
      saveFilename: 'whiteboard.png'
    };
  },

  computed: {
    ...mapState({
      minScale: state => state.whiteboard.minScale * 100,
      maxScale: state => state.whiteboard.maxScale * 100,
      brushColor: state => state.whiteboard.brushColor,
      brushWidth: state => state.whiteboard.brushWidth
    }),

    scale: {
      get() {
        return this.$store.state.whiteboard.scale * 100;
      },
      set(v) {
        this.$store.commit('whiteboard/updateStates', {scale: v/100});
      }
    },

    page: {
      get() {
        return this.$store.state.whiteboard.page;
      },
      set(v) {
        this.$store.commit('whiteboard/updateStates', {page: v});
      }
    },

    brushWidth: {
      get() {
        return this.$store.state.whiteboard.brushWidth;
      },
      set(v) {
        this.$store.commit('whiteboard/updateStates', {brushWidth: v});
      }
    },

    scaleText() {
      return parseInt(this.scale) + ' %';
    }
  },

  watch: {
    filenameMenu(v) {
      if (v) {
        this.saveFilename = 'whiteboard.png';
      }
    }
  },

  mounted() {
    // When v-menu attached on none-app element, it's position will getting wrong.
    // To fix it we need to add :menu-props property on v-select component and assign
    // 'nudgeRight'(shift to right side) value to adjust position.
    let rect = this.$refs.brushSizeSelect.$el.getBoundingClientRect();
    this.brushSizeMenuLeft = rect.left;

    this._onFullScreenChange = this.onFullScreenChange.bind(this);
    document.addEventListener('webkitfullscreenchange', this._onFullScreenChange);
    document.addEventListener('mozfullscreenchange', this._onFullScreenChange);
  },

  beforeDestroy() {
    document.removeEventListener('webkitfullscreenchange', this._onFullScreenChange);
    document.removeEventListener('mozfullscreenchange', this._onFullScreenChange);
  },

  methods: {
    zoom(isZoomIn) {
      let scaleUnit = isZoomIn ? this.scaleUnit : -this.scaleUnit;
      let scale = this.scale + scaleUnit;
      if (scale > this.maxScale) {
        this.scale = this.maxScale;
      } else if (scale < this.minScale) {
        this.scale = this.minScale;
      } else {
        this.scale = scale;
      }
    },

    previousPage() {
      if (this.page === 0) {
        return;
      }
      this.page = this.page - 1;
    },

    nextPage() {
      if (this.page >= this.maxPage - 1) {
        return;
      }
      this.page = this.page + 1;
    },

    toggleFullScreen() {
      let el = document.querySelector('html');

      if ((document.fullScreenElement && document.fullScreenElement !== null)
        || (!document.mozFullScreen && !document.webkitIsFullScreen)) {
        if (el.requestFullScreen) {
          el.requestFullScreen();
        } else if (el.mozRequestFullScreen) {
          el.mozRequestFullScreen();
        } else if (el.webkitRequestFullScreen) {
          el.webkitRequestFullScreen(Element.ALLOW_KEYBOARD_INPUT);
        }
      } else {
        if (document.cancelFullScreen) {
          document.cancelFullScreen();
        } else if (document.mozCancelFullScreen) {
          document.mozCancelFullScreen();
        } else if (document.webkitCancelFullScreen) {
          document.webkitCancelFullScreen();
        }
      }
    },

    onFullScreenChange(event) {
      if (document.fullscreenElement || document.webkitFullscreenElement || document.mozFullScreenElement) {
        this.isFullScreen = true;
      } else {
        this.isFullScreen = false;
      }
    },

    onSaveFilenameConfirmed() {
      this.filenameMenu = false;
      this.$emit('save', this.saveFilename);
    }
  }
};
</script>

<style lang="stylus" scoped>

.top-toolbar {
  border-bottom: 1px solid $def-border-color;
}

.zoom-slider {
  max-width: 15rem;
}

.zoom-slider >>> .v-input__slot {
  margin: 0;
}

.zoom-slider >>> .v-messages {
  display: none;
}

.brush-size-select {
  flex-shrink: 0;
  flex-grow: 0;
  width: 7.8rem;
}

.brush-size-option {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
}

.brush-size-option__text {
  white-space: nowrap;
  margin-right: 1rem;
  width: 5rem;
}

.brush-size-option__dot {
  margin-left: auto;
  margin-right: auto;
}
</style>
